// Shared minimal markdown renderer for chat popups (LogChatPopup,
// DoctorChatPopup, etc). Handles the small subset our LLM replies use:
//   **bold**                          → <strong>
//   `code`                            → <code>
//   # / ## / ###                      → headings
//   - / * bullet                      → bulleted list item
//   1. / 2. ordered                   → numbered list item
//   blank line                        → spacer
//   <node id="...">title</node>       → clickable chip (when opts.onNodeClick set)
//   anything else                     → paragraph
//
// CSS class names are parameterized so each consumer keeps its own theme.
// Pass a `prefix` string — every class becomes `{prefix}-md-...` and inline
// code uses `{prefix}-inline-code`. Node chips use `{prefix}-md-nodelink`.
//
// Signature accepts EITHER (text, prefix) for back-compat, OR
// (text, prefix, opts) where opts = { onNodeClick: (nodeId) => void }.

var React = require('react');
var h = React.createElement;

function renderChatMarkdown(text, prefix, opts) {
  var p = prefix || 'chat';
  var options = opts || {};
  var onNodeClick = typeof options.onNodeClick === 'function' ? options.onNodeClick : null;
  var onVariableClick = typeof options.onVariableClick === 'function' ? options.onVariableClick : null;
  var lines = String(text || '').split('\n');
  var elements = [];
  var key = 0;

  // Renders a <node id="..."> chip — clickable button when onNodeClick is wired,
  // plain span otherwise. Pulled out so chip-first extraction and the alternation
  // pass share one renderer.
  function renderNodeChip(nodeId, nodeLabel) {
    var label = nodeLabel || nodeId;
    if (onNodeClick) {
      return h('button', {
        key: key++,
        type: 'button',
        className: p + '-md-nodelink',
        onClick: function(id) { return function(e) { e.preventDefault(); e.stopPropagation(); onNodeClick(id); }; }(nodeId),
        title: 'Show on canvas'
      }, label);
    }
    return h('span', { key: key++, className: p + '-md-nodelink' }, label);
  }

  function renderVariableChip(varName, varLabel) {
    var label = varLabel || varName;
    if (onVariableClick) {
      return h('button', {
        key: key++,
        type: 'button',
        className: p + '-md-varlink',
        onClick: function(name) { return function(e) { e.preventDefault(); e.stopPropagation(); onVariableClick(name); }; }(varName),
        title: 'Highlight nodes that use this variable'
      }, label);
    }
    return h('span', { key: key++, className: p + '-md-varlink' }, label);
  }

  // Inline formatting (bold + inline code) for plain-text segments only.
  // Runs AFTER chip extraction so a model emitting **<node ...>Title</node>**
  // or `<node ...>...</node>` doesn't get its tag eaten by bold/code.
  function parseFormattingOnly(str) {
    var parts = [];
    var regex = /\*\*(.+?)\*\*|`([^`]+)`/g;
    var last = 0, m;
    while ((m = regex.exec(str)) !== null) {
      if (m.index > last) parts.push(str.slice(last, m.index));
      if (m[1] !== undefined) parts.push(h('strong', { key: key++ }, m[1]));
      else if (m[2] !== undefined) parts.push(h('code', { key: key++, className: p + '-inline-code' }, m[2]));
      last = m.index + m[0].length;
    }
    if (last < str.length) parts.push(str.slice(last));
    return parts;
  }

  function parseInline(str) {
    // Two-pass parse so chips win over markdown emphasis/code wrappers.
    // The model sometimes emits **<node id="...">Title</node>** or backtick-wrapped
    // tags — without chip-first extraction, bold/code regex swallows the literal
    // tag string and it leaks into the UI as raw "<node id=...>" text.
    // Captures: 1=nodeId, 2=nodeTitle, 3=varName, 4=varLabel
    var chipRe = /<node\s+id=["']([^"']+)["']\s*>([\s\S]*?)<\/node>|<variable\s+name=["']([^"']+)["']\s*>([\s\S]*?)<\/variable>/g;
    var parts = [];
    var last = 0, m;
    while ((m = chipRe.exec(str)) !== null) {
      if (m.index > last) {
        // Strip stray ** or ` immediately around the chip — they were emphasis
        // wrappers around the tag and are now meaningless once the chip renders.
        var pre = str.slice(last, m.index).replace(/(\*\*|`)$/, '');
        if (pre) parts.push.apply(parts, parseFormattingOnly(pre));
      }
      if (m[1] !== undefined) {
        parts.push(renderNodeChip(m[1], m[2]));
      } else if (m[3] !== undefined) {
        parts.push(renderVariableChip(m[3], m[4]));
      }
      last = m.index + m[0].length;
      // Same trim on the trailing side.
      var trailingMatch = str.slice(last).match(/^(\*\*|`)/);
      if (trailingMatch) last += trailingMatch[1].length;
    }
    if (last < str.length) {
      parts.push.apply(parts, parseFormattingOnly(str.slice(last)));
    }
    return h('span', { key: key++ }, parts);
  }

  for (var i = 0; i < lines.length; i++) {
    var line = lines[i];
    if (/^###\s/.test(line)) {
      elements.push(h('div', { key: key++, className: p + '-md-h3' }, parseInline(line.replace(/^###\s/, ''))));
    } else if (/^##\s/.test(line)) {
      elements.push(h('div', { key: key++, className: p + '-md-h2' }, parseInline(line.replace(/^##\s/, ''))));
    } else if (/^#\s/.test(line)) {
      elements.push(h('div', { key: key++, className: p + '-md-h1' }, parseInline(line.replace(/^#\s/, ''))));
    } else if (/^[-*]\s/.test(line)) {
      elements.push(h('div', { key: key++, className: p + '-md-li' },
        h('span', { className: p + '-md-bullet' }, '•'),
        h('span', { className: p + '-md-li-text' }, parseInline(line.replace(/^[-*]\s/, '')))
      ));
    } else if (/^\d+\.\s/.test(line)) {
      var num = line.match(/^(\d+)\.\s/)[1];
      elements.push(h('div', { key: key++, className: p + '-md-li' },
        h('span', { className: p + '-md-num' }, num + '.'),
        h('span', { className: p + '-md-li-text' }, parseInline(line.replace(/^\d+\.\s/, '')))
      ));
    } else if (line.trim() === '') {
      elements.push(h('div', { key: key++, className: p + '-md-spacer' }));
    } else {
      elements.push(h('div', { key: key++, className: p + '-md-p' }, parseInline(line)));
    }
  }
  return elements;
}

module.exports = { renderChatMarkdown: renderChatMarkdown };
